import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { ArrowLeft, ShieldCheck, GraduationCap, BookOpen } from "lucide-react";

const TABS = [
  {
    key: "student",
    label: "Student",
    icon: GraduationCap,
    hint: "Sign in with your student email to view your profile and raise complaints.",
    placeholder: "rohan@vasavihostel.com",
    accent: "#FFE600",
  },
  {
    key: "admin",
    label: "Admin",
    icon: ShieldCheck,
    hint: "Warden / staff access — manage students, complaints, and gallery.",
    placeholder: "admin@pghub.com",
    accent: "#FF4D4D",
  },
];

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [tab, setTab] = useState("student");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const active = TABS.find((t) => t.key === tab);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    const res = await login(email, password);
    setLoading(false);
    if (!res.ok) {
      setError(res.error);
      toast.error(res.error);
      return;
    }
    // Enforce that the tab used matches the account role
    if (tab === "admin" && res.user.role !== "admin") {
      setError("This isn't an admin account. Switch to the Student tab.");
      toast.error("Not an admin account");
      return;
    }
    if (tab === "student" && res.user.role !== "student") {
      setError("This is an admin account. Switch to the Admin tab.");
      toast.error("Use the Admin tab for this account");
      return;
    }
    toast.success(`Welcome back, ${res.user.name || res.user.email}`);
    nav(res.user.role === "admin" ? "/admin" : "/student", { replace: true });
  };

  return (
    <div className="min-h-screen bg-[#F8F9FA] flex flex-col" data-testid="login-page">
      <div className="p-6 flex items-center justify-between">
        <Link
          to="/"
          data-testid="back-home-link"
          className="inline-flex items-center gap-2 font-bold text-sm px-4 py-2 border-2 border-[#0A0A0A] rounded-full bg-white hover:shadow-brutal-sm transition-shadow"
        >
          <ArrowLeft size={16} /> Home
        </Link>
        <Link
          to="/rules"
          data-testid="rules-button"
          className="inline-flex items-center gap-2 font-bold text-sm px-4 py-2 border-2 border-[#0A0A0A] rounded-full bg-[#00E5FF] shadow-brutal-sm hover:translate-x-[1px] hover:translate-y-[1px] hover:shadow-none transition-transform"
        >
          <BookOpen size={16} /> Hostel Rules
        </Link>
      </div>
      <div className="flex-1 flex items-center justify-center px-6 pb-16">
        <div className="w-full max-w-md brutal-card p-8 md:p-10 bg-white">
          <div className="mb-6">
            <span className="text-xs font-bold uppercase tracking-[0.2em]">Welcome back</span>
            <h1 className="font-heading font-black text-4xl mt-2 tracking-tighter">Log in</h1>
          </div>

          {/* Role tabs */}
          <div
            className="flex p-1 gap-1 bg-[#F8F9FA] border-2 border-[#0A0A0A] rounded-full mb-6"
            data-testid="login-role-tabs"
            role="tablist"
          >
            {TABS.map((t) => {
              const Icon = t.icon;
              const activeTab = tab === t.key;
              return (
                <button
                  key={t.key}
                  type="button"
                  role="tab"
                  aria-selected={activeTab}
                  data-testid={`login-tab-${t.key}`}
                  onClick={() => {
                    setTab(t.key);
                    setError("");
                  }}
                  className={`flex-1 inline-flex items-center justify-center gap-2 px-4 py-2 rounded-full text-sm font-bold transition-transform ${
                    activeTab ? "border-2 border-[#0A0A0A] shadow-brutal-sm" : "border-2 border-transparent"
                  }`}
                  style={
                    activeTab
                      ? { backgroundColor: t.accent, color: t.key === "admin" ? "#fff" : "#0A0A0A" }
                      : { backgroundColor: "transparent", color: "#4A4A4A" }
                  }
                >
                  <Icon size={16} strokeWidth={2.5} />
                  {t.label}
                </button>
              );
            })}
          </div>

          <p
            className="text-sm font-medium text-[#4A4A4A] mb-5"
            data-testid={`login-tab-hint-${tab}`}
          >
            {active.hint}
          </p>

          <form onSubmit={submit} className="space-y-5" data-testid="login-form">
            <div>
              <label className="block text-xs font-bold uppercase tracking-widest mb-2">Email</label>
              <input
                data-testid="login-email-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="w-full px-4 py-3 border-2 border-[#0A0A0A] rounded-xl bg-white font-medium focus:outline-none focus:ring-2 focus:ring-[#0A0A0A] focus:ring-offset-2"
                placeholder={active.placeholder}
              />
            </div>
            <div>
              <label className="block text-xs font-bold uppercase tracking-widest mb-2">Password</label>
              <input
                data-testid="login-password-input"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="w-full px-4 py-3 border-2 border-[#0A0A0A] rounded-xl bg-white font-medium focus:outline-none focus:ring-2 focus:ring-[#0A0A0A] focus:ring-offset-2"
                placeholder="••••••••"
              />
            </div>
            {error && (
              <div
                data-testid="login-error"
                className="bg-[#FF4D4D] text-white border-2 border-[#0A0A0A] rounded-xl px-4 py-2 text-sm font-bold"
              >
                {error}
              </div>
            )}
            <button
              type="submit"
              disabled={loading}
              data-testid="login-submit-button"
              className="w-full inline-flex items-center justify-center gap-2 font-bold px-5 py-3 border-2 border-[#0A0A0A] rounded-full shadow-brutal hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-none transition-transform disabled:opacity-60"
              style={{ backgroundColor: active.accent, color: tab === "admin" ? "#fff" : "#0A0A0A" }}
            >
              {loading ? "Signing in..." : `Sign in as ${active.label}`}
            </button>
            <div className="text-center">
              <Link
                to="/forgot-password"
                data-testid="forgot-password-link"
                className="text-xs font-bold underline underline-offset-4 text-[#4A4A4A] hover:text-[#0A0A0A]"
              >
                Forgot password?
              </Link>
            </div>
          </form>

          {tab === "student" && (
            <div className="mt-6 text-sm font-medium text-[#4A4A4A]">
              New here?{" "}
              <Link to="/register" className="font-bold underline underline-offset-4">
                Create student account
              </Link>
            </div>
          )}
          {tab === "admin" && (
            <div className="mt-6 text-sm font-medium text-[#4A4A4A]">
              Need admin access?{" "}
              <Link
                to="/signup-admin"
                data-testid="admin-signup-link"
                className="font-bold underline underline-offset-4"
              >
                Create admin account
              </Link>
              <span className="text-xs block mt-1 text-[#4A4A4A]">
                (requires invite code from the hostel)
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
